import * as React from "react";
import { Link } from "react-router-dom";

import { Button, Tooltip } from "Components/ui";
import { Plug } from "Components/ui/icons";
import { cn } from "utils/cn";

/** Anything carrying a TeleOpd link state (client, fleet and billing rows, link statuses). */
export interface LinkLike {
  linked?: boolean | null;
}

/**
 * A client is "connected" once its TeleOpd link is enabled and has synced at least once (the
 * server decides `linked`). Until then every list shows it greyed out with no click-through and no
 * actions except the step that connects it: it isn't connected for a reason.
 */
export const isConnected = (link: LinkLike | null | undefined): boolean =>
  Boolean(link && link.linked);

export const NOT_CONNECTED_REASON =
  "This client's TeleOpd isn't connected to SAP yet, so there's nothing to act on here.";

/** "Not connected", with the reason on hover; shown where a connected client has its link or actions. */
export const NotConnected: React.FC<{ className?: string }> = ({ className }) => (
  <Tooltip content={NOT_CONNECTED_REASON}>
    <span
      className={cn("cursor-default whitespace-nowrap text-sm text-subtle-foreground", className)}
    >
      Not connected
    </span>
  </Tooltip>
);

/**
 * Classes that grey out a client whose TeleOpd isn't connected: muted text and colourless badges.
 * Not opacity: small text at 60% opacity drops below AA contrast, worst in dark mode.
 */
export const NOT_CONNECTED_CLASSES = "text-muted-foreground grayscale";

/** Greys out a list cell of a client whose TeleOpd isn't connected. */
export const ConnectionDim: React.FC<{
  link: LinkLike | null | undefined;
  className?: string;
  children: React.ReactNode;
}> = ({ link, className, children }) => (
  <div className={cn(!isConnected(link) && NOT_CONNECTED_CLASSES, className)}>{children}</div>
);

/** Where a client gets connected: the Connection tab of its billing page. */
export const connectTeleopdPath = (tenantId: string): string =>
  `/billing/${tenantId}?tab=connection`;

/**
 * The one action an Actionable client that isn't connected offers (needs billing/configure).
 * Locked clients can't be connected: SAP refuses to issue their keys until they are Actionable.
 */
export const ConnectTeleopdButton: React.FC<{ tenantId: string }> = ({ tenantId }) => (
  <Button asChild size="sm" variant="primary-ghost">
    <Link to={connectTeleopdPath(tenantId)}>
      <Plug aria-hidden="true" />
      Connect TeleOpd
    </Link>
  </Button>
);
